<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { errorToast } from '@/composables/useToasts';
import AlertBox from '@/components/global-themed/AlertBox.vue';

const props = defineProps({
    price: {
        type: Number,
        required: true,
    },
    currency: {
        type: String,
        default: 'GBP',
    },
    existing: {
        type: Boolean,
        default: false,
    },
});

const initialising = ref(true);
const error = ref(null);
const selectedCurrency = ref(props.currency);

const features = [
    {
        title: 'Visibility review',
        body: 'A considered review of your current presence across search, local, content, competitors and AI discovery.',
        icon: 'icons/eye-open',
    },
    {
        title: 'Opportunity mapping',
        body: 'A clear view of the areas with the most potential, from search demand and content gaps to local improvements and conversion opportunities.',
        icon: 'icons/trend-up',
    },
    {
        title: 'Prioritized direction',
        body: 'Recommendations shaped around impact, effort and relevance, so you know where to focus first.',
        icon: 'icons/keyword-target',
    },
    {
        title: '90-day roadmap',
        body: 'A practical plan for the next stage of work, giving you structure without overcomplicating the process.',
        icon: 'icons/project-plan',
    },
];

let checkout = null;

const scrollToCheckout = () => {
    document.getElementById('strategy-purchase-checkout')?.scrollIntoView({ behavior: 'smooth' });
};

const initCheckout = async () => {
    checkout?.destroy();
    checkout = null;
    initialising.value = true;
    error.value = null;

    try {
        const stripe = await loadStripe(process.env.MIX_STRIPE_KEY);

        checkout = await stripe.initEmbeddedCheckout({
            fetchClientSecret: async () => {
                const { data } = await axios.post('/strategy-purchase/checkout-session', {
                    currency: selectedCurrency.value,
                    existing: props.existing,
                });
                return data.client_secret;
            },
        });

        checkout.mount('#strategy-purchase-checkout');
    } catch {
        error.value = 'Unable to load the payment form. Please refresh and try again.';
    } finally {
        initialising.value = false;
    }
};

const switchCurrency = async (currency) => {
    if (currency === selectedCurrency.value) {
        return;
    }
    selectedCurrency.value = currency;
    await initCheckout();
};

onMounted(async () => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment_failed')) {
        errorToast('Your payment was unsuccessful. Please try again.');
        params.delete('payment_failed');
        const cleanUrl = window.location.pathname + (params.toString() ? '?' + params.toString() : '');
        history.replaceState(null, '', cleanUrl);
    }

    await initCheckout();
});

onUnmounted(() => {
    checkout?.destroy();
});
</script>

<template>
  <div class="tw-min-h-screen tw-bg-white">
    <!-- Hero -->
    <div class="tw-bg-gradient-to-b tw-from-info-200 tw-to-white">
      <div class="tw-max-w-3xl tw-mx-auto tw-px-6 tw-pt-20 tw-pb-24 tw-text-center">
        <div class="tw-flex tw-justify-center tw-p-6">
          <SvgVue
            class="tw-h-16"
            icon="logos/hike"
          />
        </div>

        <p class="tw-info-700 tw-font-semibold tw-text-xs tw-uppercase tw-tracking-widest tw-mb-5">
          Search Everywhere Strategy
        </p>

        <h1 class="tw-text-5xl tw-font-bold tw-text-neutral-900 tw-mb-6 tw-leading-tight">
          Get a bespoke plan<br>for your business growth
        </h1>

        <p class="tw-text-lg tw-text-neutral-900 tw-leading-relaxed tw-mb-10 tw-max-w-xl tw-mx-auto">
          As long as human beings seek information before they buy, SEO will always matter. But today it means Search Everywhere Optimization: not just ranking higher, but showing up everywhere people search. Smarter, faster, and in more of the places that matter.
        </p>

        <button
          type="button"
          class="tw-inline-flex tw-items-center tw-gap-2 tw-bg-indigo-600 hover:tw-bg-indigo-700 tw-text-white tw-font-semibold tw-rounded-xl tw-px-8 tw-py-4 tw-text-base tw-transition-all tw-duration-150 tw-shadow-lg hover:tw-shadow-indigo-200 hover:tw-shadow-xl"
          @click="scrollToCheckout"
        >
          Get your bespoke roadmap
          <SvgVue
            icon="icons/chevron"
            class="tw-w-3 tw-h-3"
          />
        </button>
      </div>
    </div>

    <!-- Features -->
    <div class="tw-max-w-4xl tw-mx-auto tw-px-6 tw-py-20">
      <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-5">
        <div
          v-for="feature in features"
          :key="feature.title"
          class="tw-flex tw-gap-4 tw-bg-white tw-rounded-2xl tw-p-6 tw-border tw-border-gray-100 tw-shadow-sm hover:tw-shadow-md tw-transition-shadow tw-duration-150"
        >
          <div class="tw-flex-shrink-0 tw-w-10 tw-h-10 tw-rounded-xl tw-bg-indigo-50 tw-flex tw-items-center tw-justify-center">
            <SvgVue
              :icon="feature.icon"
              class="tw-w-5 tw-h-5 tw-text-indigo-600"
            />
          </div>
          <div>
            <h3 class="tw-text-sm tw-font-semibold tw-text-neutral-900 tw-mb-1">
              {{ feature.title }}
            </h3>
            <p class="tw-text-sm tw-text-neutral-900 tw-leading-relaxed">
              {{ feature.body }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Who it's for / Outcome -->
    <div class="tw-border-y tw-border-neutral-100">
      <div class="tw-max-w-4xl tw-mx-auto tw-px-6 tw-py-12 tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-12">
        <div>
          <h2 class="tw-text-base tw-font-bold tw-text-neutral-900 tw-mb-3">
            Who it's for
          </h2>
          <p class="tw-text-sm tw-text-neutral-900 tw-leading-relaxed">
            For businesses who want to go further, faster. It helps define where you are now, where there is room to improve, and what should happen next.
          </p>
        </div>
        <div>
          <h2 class="tw-text-base tw-font-bold tw-text-neutral-900 tw-mb-3">
            The outcome
          </h2>
          <p class="tw-text-sm tw-text-neutral-900 tw-leading-relaxed">
            A focused search everywhere strategy that gives you or your team a clear direction and plan of attack for the next 90 days.
          </p>
        </div>
      </div>
    </div>

    <!-- Checkout -->
    <div class="tw-max-w-xl tw-mx-auto tw-px-6 tw-py-20">
      <div class="tw-rounded-2xl tw-border tw-border-neutral-100 tw-shadow-xl tw-overflow-hidden">
        <div class="tw-bg-white tw-p-8">
          <AlertBox
            v-if="existing"
            type="info"
            class="tw-mb-6 tw-text-sm"
          >
            This purchase will be linked to your existing Hike subscription account.
          </AlertBox>

          <div class="tw-flex tw-items-center tw-justify-between tw-mb-6">
            <p class="tw-text-sm tw-font-medium tw-text-neutral-700">
              Currency
            </p>
            <div class="tw-inline-flex tw-rounded-lg tw-border tw-border-neutral-200 tw-p-1 tw-bg-neutral-50">
              <button
                v-for="option in ['GBP', 'USD']"
                :key="option"
                type="button"
                :class="[
                  'tw-px-4 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-md tw-transition-all tw-duration-150',
                  selectedCurrency === option
                    ? 'tw-bg-primary tw-text-white tw-shadow-sm'
                    : 'tw-text-neutral-500 hover:tw-text-neutral-700',
                ]"
                @click="switchCurrency(option)"
              >
                {{ option }}
              </button>
            </div>
          </div>

          <div
            v-if="error"
            class="tw-mb-6 tw-text-sm tw-text-danger-600 tw-bg-danger-100 tw-rounded-xl tw-px-4 tw-py-3"
          >
            {{ error }}
          </div>

          <div
            v-if="initialising"
            class="tw-rounded-xl tw-p-8"
          >
            <div class="tw-h-64 tw-bg-neutral-100 tw-rounded-xl tw-animate-pulse" />
          </div>

          <div id="strategy-purchase-checkout" />
        </div>
      </div>

      <p class="tw-mt-6 tw-text-center tw-text-xs tw-text-neutral-400">
        Secured by Stripe. Your payment details are never stored on our servers.
      </p>
    </div>
  </div>
</template>
